var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MeshFacet, PrimitiveMeshFacet } from "flavr-common";
import { Box3, Matrix4 } from "flavr-math";
import { modelPool } from "@modules/model";
import { FacetHandler, FacetRemoveHandler, ModelEventHandler, ModelEventType, Service } from "@modules/module";
import { meshBoundingService } from "./MeshBoundingService";
export class ModelBoundsService extends Service {
    constructor() {
        super(...arguments);
        this.modelBounds = new Map();
    }
    /// Getters ///
    getBoundingBox(uid) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return undefined;
        const modelBoundsInfo = this.modelBounds.get(uid);
        if (modelBoundsInfo === undefined)
            return undefined;
        if (modelBoundsInfo.dirty)
            this.recomputeBounds(model);
        return modelBoundsInfo.bounds;
    }
    getAllBoundingBoxes() {
        const allBounds = [];
        for (const uid of this.modelBounds.keys()) {
            const bounds = this.getBoundingBox(uid);
            if (bounds !== undefined)
                allBounds.push(bounds);
        }
        return allBounds;
    }
    getAvailableModelUIDs() {
        return this.modelBounds.keys();
    }
    /// Event handlers ///
    handleMeshFacet(uid, facet) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const modelBoundsInfo = this.getOrCreateModelBoundsInfo(uid);
        this.computeMeshBounds(facet.meshName, facet.params.pieceName, this.resolveRoomUid(model))
            .then((bounds) => {
            var _a;
            const changed = ((bounds === undefined && modelBoundsInfo.ownBounds !== undefined) ||
                (bounds !== undefined && !(((_a = modelBoundsInfo.ownBounds) === null || _a === void 0 ? void 0 : _a.equals(bounds)) === true)));
            modelBoundsInfo.ownBounds = bounds;
            modelBoundsInfo.dirty = changed;
            this.invalidateBoundsUp(model, false);
        })
            .catch((err) => {
            console.error(err);
            modelBoundsInfo.ownBounds = undefined;
            modelBoundsInfo.dirty = true;
            this.invalidateBoundsUp(model, false);
        });
    }
    handleRemoveMeshFacet(uid, _facet) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const modelBoundsInfo = this.modelBounds.get(model.getUID());
        if (modelBoundsInfo === undefined)
            return;
        modelBoundsInfo.ownBounds = undefined;
        modelBoundsInfo.dirty = true;
        this.invalidateBoundsUp(model, false);
    }
    handlePrimitiveMeshFacet(uid, facet) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const modelBoundsInfo = this.getOrCreateModelBoundsInfo(uid);
        switch (facet.shape) {
            default:
                modelBoundsInfo.ownBounds = new Box3();
                modelBoundsInfo.dirty = true;
                break;
        }
    }
    handleRemovePrimitiveMeshFacet(uid, _facet) {
        const model = modelPool.getModel(uid);
        if (model === undefined)
            return;
        const modelBoundsInfo = this.modelBounds.get(model.getUID());
        if (modelBoundsInfo === undefined)
            return;
        modelBoundsInfo.ownBounds = undefined;
        modelBoundsInfo.dirty = true;
        this.invalidateBoundsUp(model, false);
    }
    handleModelDestroyedEvent(model) {
        this.modelBounds.delete(model.getUID());
        this.invalidateBoundsUp(model, false);
    }
    handleModelTransformUpdatedEvent(model, _localTransform) {
        const modelBoundsInfo = this.modelBounds.get(model.getUID());
        if (modelBoundsInfo === undefined)
            return;
        modelBoundsInfo.worldMatrix = model.getWorldTransform();
        modelBoundsInfo.dirty = true;
        this.recomputeBounds(model);
    }
    /// Private helper methods ///
    resolveRoomUid(model) {
        var _a;
        if (model.isRoom())
            return model.getUID();
        return (_a = model.getRoom()) === null || _a === void 0 ? void 0 : _a.getUID();
    }
    /// Private helper methods ///
    invalidateBoundsUp(model, invalidateCurrent) {
        model.traverseUp(m => {
            const boundsInfo = this.modelBounds.get(m.getUID());
            if (boundsInfo === undefined)
                return;
            boundsInfo.dirty = true;
        }, invalidateCurrent);
    }
    recomputeBounds(model) {
        const newBounds = new Box3();
        const modelBoundsInfo = this.modelBounds.get(model.getUID());
        if (modelBoundsInfo === undefined)
            return undefined;
        if (!modelBoundsInfo.dirty)
            return modelBoundsInfo.bounds;
        if (modelBoundsInfo.ownBounds !== undefined) {
            newBounds.copy(modelBoundsInfo.ownBounds);
            newBounds.applyMatrix4(model.getWorldTransform());
        }
        for (const child of model.getChildren()) {
            const childBounds = this.recomputeBounds(child);
            if (childBounds !== undefined && !childBounds.isEmpty())
                newBounds.union(childBounds);
        }
        modelBoundsInfo.bounds = newBounds;
        return newBounds;
    }
    /// Private helper methods ///
    getOrCreateModelBoundsInfo(uid) {
        let modelBoundsInfo = this.modelBounds.get(uid);
        if (modelBoundsInfo !== undefined)
            return modelBoundsInfo;
        modelBoundsInfo = {
            bounds: new Box3(),
            ownBounds: undefined,
            worldMatrix: new Matrix4(),
            dirty: true
        };
        this.modelBounds.set(uid, modelBoundsInfo);
        return modelBoundsInfo;
    }
    computeMeshBounds(meshName, pieceName, roomUid) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = meshBoundingService.generateFullMeshUrl(meshName, pieceName);
            const data = yield meshBoundingService.getBoundingOnceFinished(url, roomUid);
            if (data === undefined)
                return undefined;
            return data.boundingBox;
        });
    }
}
__decorate([
    FacetHandler(MeshFacet)
], ModelBoundsService.prototype, "handleMeshFacet", null);
__decorate([
    FacetRemoveHandler(MeshFacet)
], ModelBoundsService.prototype, "handleRemoveMeshFacet", null);
__decorate([
    FacetHandler(PrimitiveMeshFacet)
], ModelBoundsService.prototype, "handlePrimitiveMeshFacet", null);
__decorate([
    FacetRemoveHandler(PrimitiveMeshFacet)
], ModelBoundsService.prototype, "handleRemovePrimitiveMeshFacet", null);
__decorate([
    ModelEventHandler(ModelEventType.MODEL_DESTROYED)
], ModelBoundsService.prototype, "handleModelDestroyedEvent", null);
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], ModelBoundsService.prototype, "handleModelTransformUpdatedEvent", null);
